import { AlertCircle, CheckCircle } from '@recruit-robin/robin-theme/web-icons';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { ReactComponent as Moped } from '../assets/moped.svg';
import { useReducedMotion } from '../lib';
import { hopHeight, hopKeyframes, hopTiming, roadCss } from './road.styles';

/** Let the exit arrive and settle, then cut the engine and fade the road. */
const POWER_DOWN_DELAY_MS = 1500;

/** The slide, the settle and the badge are all done by now — see `arrivalOpen`. */
const ARRIVAL_SETTLED_MS = 1600;

/** The hop clears the brand bar by this much, and never reads as less than this tall. */
const SKY_GAP_PX = 10;
const MIN_SKY_PX = 14;

/**
 * The clear sky above the scooter, measured when the hop is asked for rather than on mount — it
 * costs one read and it cannot be wrong about a layout that has since reflowed.
 *
 * The ceiling is the stage rather than whatever precedes this road, which is how the tracker's own
 * version of this works: this road is the first thing in the card, so there is nothing above it to
 * measure against. The stage's top padding *is* the sky, and it is 40px expanded against 24px
 * compact — reading it keeps the hop in proportion on a phone and off the brand bar, which a single
 * constant could not do for both.
 */
const readHeadroom = (root: HTMLElement | null, rider: HTMLElement | null) => {
  if (!root || !rider) return MIN_SKY_PX;

  const stage = root.closest('[data-af-stage]');
  const ceilingBottom = (stage ?? root).getBoundingClientRect().top;

  return Math.max(MIN_SKY_PX, Math.round(rider.getBoundingClientRect().top - ceilingBottom - SKY_GAP_PX));
};

export type ApplyArrivalRoadHandle = {
  /**
   * Plays the scooter's hop for a rating of 1–5, as high as the score, with the backflip at five.
   * Imperative because it answers a tap: the same rating tapped twice has to hop twice, and a prop
   * carrying the same value again would not.
   */
  celebrate: (rating: number) => void;
};

export type ApplyArrivalRoadProps = {
  /**
   * `check` for Booked, `alert` for the exit. Same composition either way, one glyph
   * swapped — that sameness is what makes them read as a pair.
   */
  badge: 'check' | 'alert';
  /**
   * The exit branch. Identical to Booked on the way in, then it powers down: the engine
   * jitter stops and the trail fades, so the scooter stays but the ride is visibly over.
   */
  isExit?: boolean;
  /** Plays the arrival once per visit, not again on a re-render or a language switch. */
  isPlaying?: boolean;
};

/**
 * The terminal-screen road: literally the progress bar run to 100%, with a status badge.
 *
 * Base styles are the finished state; `isPlaying` replays the completion. The whole group
 * slides in from beyond the left edge under one transform, so the scooter and its trail
 * cannot drift apart.
 *
 * A screen that asks the candidate something — the process-feedback panel — can take the handle and
 * have the scooter answer the rating. The hop itself is the progress bar's and the tracker's, out of
 * `road.styles`, so the three surfaces cannot drift apart either.
 */
export const ApplyArrivalRoad = forwardRef<ApplyArrivalRoadHandle, ApplyArrivalRoadProps>(
  ({ badge, isExit = false, isPlaying = false }, ref) => {
    const isReducedMotion = useReducedMotion();
    // The exit's badge is held back until the scooter has parked; Booked's is simply
    // delayed past its own arrival.
    const [isParked, setIsParked] = useState(!isExit || !isPlaying || isReducedMotion);
    /** Nothing to clip when the arrival is not being played: the group is already at rest. */
    const [hasLanded, setHasLanded] = useState(!isPlaying || isReducedMotion);

    const rootRef = useRef<HTMLDivElement>(null);
    const riderRef = useRef<HTMLSpanElement>(null);
    /**
     * The hop lands on the vehicle, never on the rider above it: the rider carries this road's
     * own `left` offset and its settle animation, and a second `transform` there would replace
     * rather than add. The vehicle is also where the idle jitter lives, which is what lets a
     * script animation take over and hand back.
     */
    const vehicleRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
      if (isParked) return;

      const timeout = setTimeout(() => setIsParked(true), POWER_DOWN_DELAY_MS);

      return () => clearTimeout(timeout);
    }, [isParked]);

    useEffect(() => {
      if (hasLanded) return;

      const timeout = setTimeout(() => setHasLanded(true), ARRIVAL_SETTLED_MS);

      return () => clearTimeout(timeout);
    }, [hasLanded]);

    useImperativeHandle(
      ref,
      () => ({
        celebrate: (rating: number) => {
          const vehicle = vehicleRef.current;

          if (!vehicle || !vehicle.animate || isReducedMotion || rating <= 0) return;

          const headroom = readHeadroom(rootRef.current, riderRef.current);

          vehicle.animate(hopKeyframes(hopHeight(headroom, rating), rating), hopTiming(rating));
        },
      }),
      [isReducedMotion],
    );

    const Glyph = badge === 'check' ? CheckCircle : AlertCircle;

    return (
      <div ref={rootRef} css={[roadCss.arrival, hasLanded && roadCss.arrivalOpen]} aria-hidden="true">
        <div css={[roadCss.track, roadCss.arrivalTrack, isPlaying && roadCss.arrivalPlay]}>
          <div
            css={[
              roadCss.fill,
              roadCss.arrivalFill,
              isExit && isParked && (isPlaying ? roadCss.parkedFill : roadCss.badgeHidden),
            ]}
          />
          <span
            ref={riderRef}
            css={[roadCss.rider, roadCss.riderLarge, roadCss.arrivalRider, isPlaying && roadCss.arrivalPlayRider]}>
            <span
              ref={vehicleRef}
              css={[roadCss.vehicle, roadCss.vehicleLarge, isExit && isParked && roadCss.parkedVehicle]}>
              <Moped />
            </span>
            <span
              css={[
                roadCss.badge,
                !isParked && roadCss.badgeHidden,
                isParked && (isExit ? roadCss.badgeIn : roadCss.badgeInDelayed),
              ]}>
              <Glyph size={18} color="currentColor" />
            </span>
          </span>
        </div>
      </div>
    );
  },
);

ApplyArrivalRoad.displayName = 'ApplyArrivalRoad';
